import { PlusIcon } from '@heroicons/react/24/outline'
import { getBorrowerInitials } from 'components/GlobalLoanSearch/utils'
import { LayoutLoading } from 'components/LayoutLoading'
import { TextHighlighter } from 'components/TextHighlighter'
import type { IAccountingVendor } from 'pages/Admin/Accounting/AccountingVendors/types'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getAccountingVendors } from 'services'
import { Input2 } from 'stories/components'

/** Someone on the loan who can be paid (from GET /loan/payables/payees). */
export interface LoanParty {
  payeeType: string
  payeeRefId: number | null
  name: string
  address: string
  achReady: boolean
  achBlockedReason: string
}

/** A payee the modal will raise a payment to, normalized to the shape a payable needs. */
export interface ChosenPayee {
  payeeType: string
  payeeRefId: number | null
  name: string
  address: string
  achReady?: boolean
  achBlockedReason?: string
}

const VENDOR_RESULT_LIMIT = 15

/** A flat, keyboard-navigable row: a loan party, a searched vendor, or the free-text fallback. */
type Row =
  | { kind: 'party'; party: LoanParty }
  | { kind: 'vendor'; vendor: IAccountingVendor }
  | { kind: 'freeText'; name: string }

/**
 * Type-ahead payee picker used wherever someone is chosen to be paid (the Pay a Party modal, the
 * Other Payments editor). Parties on the loan (borrower, participants,
 * vendors, contractors) are pinned at the top of the lookup; typing also searches the global
 * accounting-vendor book, and anything typed can be used verbatim as a one-off payee.
 */
export const PartyPayeeLookup = ({
  parties,
  value,
  title = 'Who is being paid',
  required = false,
  error = '',
  disabled = false,
  onSelect,
}: {
  parties: LoanParty[]
  value: ChosenPayee | null
  title?: string
  required?: boolean
  error?: string
  disabled?: boolean
  onSelect: (payee: ChosenPayee | null) => void
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState(value?.name || '')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [vendors, setVendors] = useState<IAccountingVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  // A programmatic query change (a selection, or a prefill) must not trigger another search.
  const skipSearchRef = useRef(false)
  /**
   * Where to draw the results, in viewport coordinates.
   *
   * Through a portal rather than as an absolutely positioned child, because this control sits
   * inside Modal on the Draw screen and the Pay a Party modal: the panel is `overflow-auto` with
   * a max height and the body is `overflow-x-auto`, and setting one overflow axis makes the other
   * a clipping box too. On the Draw modal the field is near the bottom edge, so the list was
   * clipped away entirely rather than merely cut short.
   */
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null)

  // Re-sync the box when a different payee arrives from the parent (a prefill, or a row being
  // re-edited). Keyed on the payee's identity rather than the object, since a caller that derives
  // `value` on each render would otherwise re-run this on every keystroke. A null `value` is left
  // alone deliberately: it means the typed text no longer matches the chosen payee, and blanking
  // the box mid-word would eat what is being typed.
  const valueKey = value ? `${value.payeeType}:${value.payeeRefId ?? ''}:${value.name}` : ''
  useEffect(() => {
    if (!valueKey) return
    skipSearchRef.current = true
    setQuery(value?.name || '')
  }, [valueKey])

  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false
      setDebouncedQuery('')
      return
    }
    const timeoutId = setTimeout(() => setDebouncedQuery(query.trim()), 400)
    return () => clearTimeout(timeoutId)
  }, [query])

  // Remote vendor search, gated on the dropdown being open and something being typed.
  useEffect(() => {
    if (!debouncedQuery || !isOpen) {
      setVendors([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    getAccountingVendors({
      query: debouncedQuery,
      orderBy: 'name',
      orderDir: '1',
      pageNum: 0,
      count: VENDOR_RESULT_LIMIT,
      activeOnly: true,
    })
      .then((data: any) => {
        if (cancelled) return
        setVendors(Array.isArray(data?.results) ? data.results : [])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // The list is in a portal, so it is not inside `containerRef`; without this a click on a
      // result would close it before the click could select anything.
      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /** Keep the portal under the field as the page, or any scroll container around it, moves. */
  const positionDropdown = useCallback(() => {
    const box = containerRef.current?.getBoundingClientRect()
    if (box) setAnchor({ top: box.bottom, left: box.left, width: box.width })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return
    positionDropdown()
    // Capture phase, so this also fires for scrolling inside the modal panel.
    window.addEventListener('scroll', positionDropdown, true)
    window.addEventListener('resize', positionDropdown)
    return () => {
      window.removeEventListener('scroll', positionDropdown, true)
      window.removeEventListener('resize', positionDropdown)
    }
  }, [isOpen, positionDropdown])

  const trimmedQuery = query.trim()

  // Parties on the loan, filtered by the query but always shown ahead of vendor results.
  const filteredParties = useMemo(() => {
    if (!trimmedQuery) return parties
    const q = trimmedQuery.toLowerCase()
    return parties.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.payeeType || '').toLowerCase().includes(q),
    )
  }, [parties, trimmedQuery])

  const hasExactMatch = useMemo(() => {
    const q = trimmedQuery.toLowerCase()
    return (
      filteredParties.some((p) => p.name.toLowerCase() === q) ||
      vendors.some((v) => (v.name || '').toLowerCase() === q)
    )
  }, [filteredParties, vendors, trimmedQuery])

  // The single flat list the arrow keys walk: parties, then vendors, then the free-text row.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = filteredParties.map((party) => ({ kind: 'party', party }))
    vendors.forEach((vendor) => out.push({ kind: 'vendor', vendor }))
    if (trimmedQuery && !hasExactMatch) out.push({ kind: 'freeText', name: trimmedQuery })
    return out
  }, [filteredParties, vendors, trimmedQuery, hasExactMatch])

  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery, vendors, isOpen])

  const commit = (row: Row) => {
    skipSearchRef.current = true
    setIsOpen(false)
    setVendors([])
    if (row.kind === 'party') {
      const p = row.party
      setQuery(p.name)
      onSelect({
        payeeType: p.payeeType,
        payeeRefId: p.payeeRefId,
        name: p.name,
        address: p.address,
        achReady: p.achReady,
        achBlockedReason: p.achBlockedReason,
      })
    } else if (row.kind === 'vendor') {
      const v = row.vendor
      setQuery(v.name || '')
      // Carry the vendor's id, not just their name: the payout queued for this payee resolves its
      // banking (and its payment history) from that id, so a vendor searched out of the global book
      // is payable by ACH exactly like one already on the loan.
      onSelect({
        payeeType: 'Vendor',
        payeeRefId: Number(v.id) || null,
        name: v.name || '',
        address: v.address || '',
      })
    } else {
      setQuery(row.name)
      onSelect({ payeeType: 'Other', payeeRefId: null, name: row.name, address: '' })
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!rows.length) return
      setActiveIndex((prev) => Math.min(prev + 1, rows.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!rows.length) return
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (event.key === 'Enter') {
      if (rows[activeIndex]) {
        event.preventDefault()
        commit(rows[activeIndex])
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false)
    }
  }

  let rowIndex = -1

  return (
    <div className="PartyPayeeLookup-container relative" ref={containerRef}>
      <Input2
        type="text"
        title={title}
        required={required}
        error={error}
        disabled={disabled}
        value={query}
        placeholder="Search parties on this loan, or any vendor"
        onChange={(nextValue: string) => {
          setQuery(nextValue)
          setIsOpen(true)
          // What's in the box no longer matches the chosen payee until they pick again.
          if (value) onSelect(null)
        }}
        onFocus={() => !disabled && setIsOpen(true)}
        onKeyDown={onKeyDown}
      />
      {isOpen &&
        !disabled &&
        anchor &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: anchor ? anchor.top + 4 : 0,
              left: anchor ? anchor.left : 0,
              width: anchor ? Math.round(anchor.width) : undefined,
            }}
            className="z-[100]"
          >
            <div className="relative overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-2xl">
            <LayoutLoading show={loading} />
            <div className="max-h-80 overflow-y-auto p-1.5">
              {filteredParties.length > 0 && (
                <>
                  <div className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    On this loan
                  </div>
                  {filteredParties.map((party) => {
                    rowIndex += 1
                    const index = rowIndex
                    return (
                      <PartyRow
                        key={`party-${party.payeeType}-${party.payeeRefId ?? party.name}`}
                        party={party}
                        query={query}
                        isActive={index === activeIndex}
                        onMouseEnter={() => setActiveIndex(index)}
                        onSelect={() => commit({ kind: 'party', party })}
                      />
                    )
                  })}
                </>
              )}

              {vendors.length > 0 && (
                <>
                  <div className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Vendors
                  </div>
                  {vendors.map((vendor, i) => {
                    rowIndex += 1
                    const index = rowIndex
                    return (
                      <VendorRow
                        key={vendor.id || `vendor-${i}`}
                        vendor={vendor}
                        query={query}
                        isActive={index === activeIndex}
                        onMouseEnter={() => setActiveIndex(index)}
                        onSelect={() => commit({ kind: 'vendor', vendor })}
                      />
                    )
                  })}
                </>
              )}

              {!loading && !filteredParties.length && !vendors.length && trimmedQuery && !hasExactMatch && (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  No parties or vendors match &quot;{trimmedQuery}&quot;.
                </div>
              )}
              {!trimmedQuery && !filteredParties.length && (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  Start typing to search vendors.
                </div>
              )}
            </div>

            {trimmedQuery && !hasExactMatch && (
              <button
                type="button"
                className={`flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-left text-sm font-medium text-shade-blue ${
                  activeIndex === rows.length - 1 ? 'bg-sky-50' : 'hover:bg-gray-50'
                }`}
                onMouseEnter={() => setActiveIndex(rows.length - 1)}
                onMouseDown={(event) => {
                  event.preventDefault()
                  commit({ kind: 'freeText', name: trimmedQuery })
                }}
              >
                <PlusIcon className="h-4 w-4" />
                <span>
                  Pay <span className="font-semibold">&quot;{trimmedQuery}&quot;</span> (one-off)
                </span>
              </button>
            )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

const PartyRow = ({
  party,
  query,
  isActive,
  onMouseEnter,
  onSelect,
}: {
  party: LoanParty
  query: string
  isActive: boolean
  onMouseEnter: () => void
  onSelect: () => void
}) => (
  <button
    type="button"
    className={`group w-full rounded-lg px-2.5 py-2.5 text-left transition-colors focus:outline-none ${
      isActive ? 'bg-sky-50' : 'hover:bg-gray-50'
    }`}
    onMouseEnter={onMouseEnter}
    onMouseDown={(event) => {
      event.preventDefault()
      onSelect()
    }}
  >
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">
        {getBorrowerInitials(party.name || '?')}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-900">
          <TextHighlighter searchText={query} text={party.name || ''} />
        </div>
        {party.address && <div className="mt-0.5 truncate text-xs text-gray-500">{party.address}</div>}
      </div>
      <div className="shrink-0 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
        {party.payeeType}
      </div>
    </div>
  </button>
)

const VendorRow = ({
  vendor,
  query,
  isActive,
  onMouseEnter,
  onSelect,
}: {
  vendor: IAccountingVendor
  query: string
  isActive: boolean
  onMouseEnter: () => void
  onSelect: () => void
}) => (
  <button
    type="button"
    className={`group w-full rounded-lg px-2.5 py-2.5 text-left transition-colors focus:outline-none ${
      isActive ? 'bg-sky-50' : 'hover:bg-gray-50'
    }`}
    onMouseEnter={onMouseEnter}
    onMouseDown={(event) => {
      event.preventDefault()
      onSelect()
    }}
  >
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
        {getBorrowerInitials(vendor.name || '?')}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-900">
          <TextHighlighter searchText={query} text={vendor.name || ''} />
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
          {vendor.ein && (
            <span className="shrink-0 font-medium tabular-nums text-gray-400">
              <TextHighlighter searchText={query} text={vendor.ein} />
            </span>
          )}
          {vendor.ein && vendor.address && <span className="shrink-0 text-gray-300">·</span>}
          {vendor.address && (
            <span className="truncate">
              <TextHighlighter searchText={query} text={vendor.address} />
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
        Vendor
      </div>
    </div>
  </button>
)
